import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Input,
  Button,
  Avatar,
  Spin,
  List,
  message as antdMessage,
  Tooltip,
  Image,
  Badge,
  FloatButton,
} from "antd";
import {
  SendOutlined,
  WhatsAppOutlined,
  ReloadOutlined,
  LoadingOutlined,
  CrownOutlined,
  UserOutlined,
  RobotOutlined,
  TeamOutlined,
  SearchOutlined,
  SmileOutlined,
  UnlockOutlined,
  CustomerServiceOutlined,
  ArrowLeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CommentOutlined,
  BarsOutlined,
} from "@ant-design/icons";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import webSocketService from "../../../services/WebSocketService";
import { get, post, put } from "../../../Api/http/httpClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import moment from "moment";
import "./WhatsAppChat.css";

/* Interfaces y Funciones Auxiliares */

interface Message {
  id: string;
  text: string;
  timestamp: string; // ISO string
  role: "user" | "assistant" | "agent";
  message_type?: string;
  loading?: boolean;
}

interface ChatItem {
  chat_uuid: string;
  name_user: string;
  is_controlled: boolean;
  history: Message[];
}

interface WhatsAppChatProps {
  user_data: {
    "custom:establishment_id": string;
    "custom:wppConfigured": string;
    "custom:plan": string;
    "family_name": string;
  };
}

const generateUniqueId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const formatTimestamp = (timestamp: string): string => {
  return moment(timestamp).format("DD/MM/YYYY HH:mm:ss");
};

/* Función para renderizar el contenido del mensaje según su tipo */
const formatMessageContent = (
  message: Message,
  setPreviewImage: (src: string) => void
) => {
  const { text, message_type } = message;

  if (message_type === "image") {
    return (
      <Image
        src={text}
        alt="Imagen enviada"
        className="whatsapp-chat-image"
        style={{ cursor: "pointer" }}
        width={200} // Ajusta el tamaño según tus necesidades
        preview={{
          onVisibleChange: (visible) => {
            if (visible) {
              setPreviewImage(text);
            }
          },
        }}
      />
    );
  }

  if (message_type === "audio") {
    return (
      <audio controls className="whatsapp-chat-audio">
        <source src={text} type="audio/mpeg" />
        Tu navegador no soporta el elemento de audio.
      </audio>
    );
  }

  if (message_type === "document") {
    // Extract filename from URL if possible
    let fileName = "Documento";
    try {
      const url = new URL(text);
      const pathParts = url.pathname.split('/');
      if (pathParts.length > 0) {
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart) fileName = decodeURIComponent(lastPart);
      }
    } catch (e) {
      // If URL parsing fails, use generic name
    }
    
    return (
      <div className="whatsapp-chat-document-container">
        <div className="whatsapp-document-icon">📄</div>
        <div className="whatsapp-document-details">
          <div className="whatsapp-document-name">{fileName}</div>
          <div className="whatsapp-document-size">Documento adjunto</div>
        </div>
        <a
          href={text}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-document-download"
        >
          Descargar
        </a>
      </div>
    );
  }

  // Asume 'text' si no está presente o cualquier otro tipo no manejado
  return (
    <ReactMarkdown
      children={text}
      remarkPlugins={[remarkGfm]}
      components={{
        img: ({ node, ...props }) => (
          <Image
            src={props.src || ""}
            alt={props.alt || "Imagen enviada"}
            className="whatsapp-chat-image"
            style={{ cursor: "pointer" }}
            width={200} // Ajusta el tamaño según tus necesidades
            preview={{
              onVisibleChange: (visible) => {
                if (visible) {
                  setPreviewImage(props.src || "");
                }
              },
            }}
          />
        ),
        ul: ({ node, ...props }) => (
          <ul style={{ paddingLeft: "20px", marginTop: "10px" }} {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol style={{ paddingLeft: "20px", marginTop: "10px" }} {...props} />
        ),
        li: ({ node, ...props }) => (
          <li style={{ marginBottom: "5px" }} {...props} />
        ),
        p: ({ node, ...props }) => <p style={{ margin: "5px 0" }} {...props} />,
      }}
    />
  );
};

/** Tipos para el mensaje individual */
interface ChatMessageProps {
  message: Message;
  setPreviewImage: React.Dispatch<React.SetStateAction<string>>;
}

/* Componente memoizado para mensajes individuales */
const ChatMessage = React.memo(({ message, setPreviewImage }: ChatMessageProps) => {
  const { role, timestamp, loading, id } = message;
  
  return (
    <div
      className={`whatsapp-chat-message ${
        role === "assistant"
          ? "whatsapp-received"
          : role === "agent"
          ? "whatsapp-agent-received"
          : "whatsapp-sent"
      }`}
    >
      {/* Avatar según el rol */}
      {role === "user" && (
        <Avatar
          icon={<UserOutlined />}
          className="whatsapp-message-avatar"
          style={{ backgroundColor: "#52c41a" }}
        />
      )}

      {role === "assistant" && (
        <Avatar
          icon={<RobotOutlined />}
          className="whatsapp-message-avatar"
          style={{ backgroundColor: "#273c75" }}
        />
      )}

      {role === "agent" && (
        <Avatar
          icon={<TeamOutlined />}
          className="whatsapp-message-avatar"
          style={{ backgroundColor: "#44bd32" }}
        />
      )}

      {/* Contenido de la burbuja */}
      <div className="whatsapp-chat-message-content">
        {loading ? (
          <Spin />
        ) : (
          formatMessageContent(message, setPreviewImage)
        )}
        <div className="whatsapp-chat-message-time">
          {formatTimestamp(timestamp)}
        </div>
      </div>
    </div>
  );
});

// Interfaz para las props del componente ChatInput
interface ChatInputProps {
  newMessage: string;
  setNewMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: () => void;
  isSending: boolean;
  isConnected: boolean;
  showEmojiPicker: boolean;
  toggleEmojiPicker: () => void;
  addEmoji: (emoji: any) => void;
  data: any;
  windowWidth: number;
  onFocus: () => void;
  onBlur: () => void;
}

// Componente para el área de input
const ChatInput = React.memo(({ 
  newMessage,
  setNewMessage,
  handleSendMessage,
  isSending,
  isConnected,
  showEmojiPicker,
  toggleEmojiPicker,
  addEmoji,
  data,
  windowWidth,
  onFocus,
  onBlur
}: ChatInputProps) => {
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Usando requestAnimationFrame para optimizar actualizaciones de DOM
    window.requestAnimationFrame(() => {
      setNewMessage(value);
    });
  }, [setNewMessage]);

  // Cerrar el emoji picker cuando se hace clic fuera
  const pickerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        // Si el click fue dentro del botón de emoji, no cerrarlo (toggleEmojiPicker lo manejará)
        const target = event.target as HTMLElement;
        if (target.closest('.whatsapp-emoji-button')) return;
        
        if (showEmojiPicker) toggleEmojiPicker();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker, toggleEmojiPicker]);

  return (
    <div className="whatsapp-whatsapp-chat-input">
      <div className="whatsapp-input-container">
        <Input
          value={newMessage}
          onChange={handleInputChange}
          placeholder="Escribe un mensaje"
          onPressEnter={handleSendMessage}
          disabled={isSending || !isConnected}
          className="whatsapp-message-input"
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <Button
          type="text"
          icon={<SmileOutlined />}
          onClick={toggleEmojiPicker}
          className="whatsapp-emoji-button"
        />
        {showEmojiPicker && (
          <div ref={pickerRef} className="whatsapp-emoji-picker">
            <Picker 
              data={data} 
              onEmojiSelect={addEmoji} 
              theme="light" 
              previewPosition={windowWidth <= 768 ? "none" : "top"}
              skinTonePosition={windowWidth <= 768 ? "none" : "bottom"}
            />
          </div>
        )}
      </div>
      <Button
        type="primary"
        shape="circle"
        icon={isSending ? <LoadingOutlined className="whatsapp-send-loading-icon" /> : <SendOutlined className="whatsapp-send-icon" />}
        onClick={handleSendMessage}
        disabled={!isConnected || isSending || !newMessage.trim()}
        className="whatsapp-chat-send-button"
        title="Enviar mensaje"
      />
    </div>
  );
});

const WhatsAppChat: React.FC<WhatsAppChatProps> = ({ user_data }) => {
  /* Estados principales */
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [chatList, setChatList] = useState<ChatItem[]>([]);
  const [selectedChatUUID, setSelectedChatUUID] = useState<string | null>(null);
  const [loadingChats, setLoadingChats] = useState<boolean>(false);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isMobileChatView, setIsMobileChatView] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [isSwiping, setIsSwiping] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  // Estado para las reconexiones
  const [lastReconnectAttempt, setLastReconnectAttempt] = useState<number>(0);

  // Nuevo estado para manejar la carga del botón de liberar chat
  const [isLiberating, setIsLiberating] = useState<boolean>(false);

  // Estado para manejar la imagen en el previsualizador de Ant Design
  const [previewImage, setPreviewImage] = useState<string>("");
  const [lastActive, setLastActive] = useState<string>(""); // New state for last active

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Refs para los gestos táctiles
  const touchStartXRef = useRef<number>(0);
  const touchEndXRef = useRef<number>(0);
  
  // Manejo de gestos táctiles
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    // Detectar dirección del swipe
    const swipeDistance = touchEndXRef.current - touchStartXRef.current;
    const minSwipeDistance = 100; // Mínima distancia para considerar como swipe
    
    // Solo en móvil y si el swipe es suficientemente largo
    if (windowWidth <= 768 && Math.abs(swipeDistance) > minSwipeDistance) {
      // Swipe a la derecha (abrir lista de chats)
      if (swipeDistance > 0 && isMobileChatView) {
        setIsMobileChatView(false);
      } 
      // Swipe a la izquierda (ver chat activo)
      else if (swipeDistance < 0 && !isMobileChatView && selectedChatUUID) {
        setIsMobileChatView(true);
      }
    }
  }, [windowWidth, isMobileChatView, selectedChatUUID]);

  /* Función para enviar un mensaje a WhatsApp */
  const sendMessageToWhatsApp = async () => {
    if (!newMessage.trim()) return;
    setIsSending(true);

    // Timeout de seguridad para limpiar el estado isSending
    const timeoutId = setTimeout(() => {
      setIsSending(false);
    }, 10000); // 10 segundos como máximo

    try {
      const res = await post("/chat-ai", {
        establishment_id: user_data["custom:establishment_id"],
        name_establishment: user_data["family_name"],
        chat_uuid: selectedChatUUID,
        whatsapp_message: newMessage,
        whatsapp_send: true,
      });

      const answer = res.data?.answer ?? res.answer;

      if (answer) {
        // Add message to the UI only after successful response
        const newMsg: Message = {
          id: generateUniqueId(),
          text: newMessage,
          timestamp: new Date().toISOString(),
          role: "agent",
        };
        setMessages((prevMessages) => [...prevMessages, newMsg]);
        setNewMessage("");
        
        antdMessage.success(
          res.data?.message || res.message || "Mensaje enviado correctamente."
        );
      } else {
        antdMessage.error("Error al enviar el mensaje a WhatsApp.");
      }
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      antdMessage.error("Error al conectar con la API.");
    } finally {
      clearTimeout(timeoutId);
      setIsSending(false);
    }
  };

  /* Optimizar actualizaciones de estado */
  const handleSendMessage = useCallback(() => {
    if (!isConnected || !selectedChatUUID) {
      antdMessage.warning(
        "Por favor, selecciona un chat antes de enviar un mensaje."
      );
      return;
    }
    if (newMessage.trim() !== "") {
      sendMessageToWhatsApp();
    }
  }, [isConnected, selectedChatUUID, newMessage]);

  /* Obtener la lista completa de chats */
  const getChatData = async () => {
    setLoadingChats(true);
    try {
      const res = await get(
        `/chat-ai?establishmentID=${user_data["custom:establishment_id"]}&return_all=true&channel_filter=whatsapp`
      );

      if (res.answer && res.items) {
        setChatList(
          res.items.map((chat: any) => ({
            chat_uuid: chat.chat_uuid,
            name_user: chat.name_user || "Usuario no identificado",
            is_controlled: chat.is_controlled,
            history: chat.history.map((msg: any) => ({
              id: generateUniqueId(),
              text: msg.message,
              timestamp: msg.timestamp,
              role: msg.role as "user" | "assistant" | "agent",
              message_type: msg.message_type || "text", // Asume 'text' si no está presente
            })),
          }))
        );
      } else {
        antdMessage.error("No se encontraron chats.");
      }
    } catch (error) {
      antdMessage.error("Error al obtener los datos de chats.");
    } finally {
      setLoadingChats(false);
    }
  };

  /* Obtener el historial de un chat específico */
  const fetchChatHistory = async (chatUUID: string) => {
    setLoadingHistory(true);
    try {
      const res = await get(
        `/chat-ai?establishmentID=${user_data["custom:establishment_id"]}&chat_uuid=${chatUUID}`
      );

      if (res.answer && res.data) {
        const combinedMessages: Message[] = res.data.history
          .filter(
            (item: any) =>
              item.message !== "ß-Mensaje enviado por la plataforma-ß"
          )
          .map((item: any) => ({
            id: generateUniqueId(),
            text: item.message,
            timestamp: item.timestamp,
            role: item.role as "user" | "assistant" | "agent",
            message_type: item.message_type || "text", // Asume 'text' si no está presente
          }));

        setMessages(combinedMessages);
        
        // Set last active time based on the latest user message
        const userMessages = combinedMessages.filter(msg => msg.role === "user");
        if (userMessages.length > 0) {
          const latestMessage = userMessages[userMessages.length - 1];
          setLastActive(latestMessage.timestamp);
        }
      } else {
        antdMessage.error("No se encontraron mensajes para este chat.");
      }
    } catch (error) {
      antdMessage.error("Error al obtener el historial de chat.");
    } finally {
      setLoadingHistory(false);
    }
  };

  /* Conectar WebSocket a un chat (por su UUID) */
  const connectWebSocket = (chat_uuid: string) => {
    if (!chat_uuid) return;

    webSocketService.connect(
      user_data,
      chat_uuid,
      "CHAT",
      null,
      () => {
        setIsConnected(true);
        setIsConnecting(false);
      },
      () => {
        // Solo mostramos el mensaje de error si no estamos en proceso de cambio de chat
        if (!isConnecting) {
          setIsConnected(false);
          setIsConnecting(false);
          antdMessage.error("Se ha perdido la conexión. Intenta reconectar.");
        }
      },
      () => {
        // Solo mostramos el mensaje de error si no estamos en proceso de cambio de chat
        if (!isConnecting) {
          setIsConnected(false);
          setIsConnecting(false);
          antdMessage.error("Error en la conexión. Intenta reconectar.");
        }
      }
    );
  };

  // Tiempo de reconexión para las re-conexiones automáticas de WebSocket
  useEffect(() => {
    const RECONNECT_DELAY = 15000; // 15 segundos entre intentos visibles al usuario
    
    const checkConnection = () => {
      const now = Date.now();
      
      // Si no está conectado y no está en proceso de conexión y ha pasado suficiente tiempo desde el último intento
      if (!isConnected && !isConnecting && selectedChatUUID && (now - lastReconnectAttempt > RECONNECT_DELAY)) {
        setLastReconnectAttempt(now);
        antdMessage.info("Verificando conexión WebSocket...");
        connectWebSocket(selectedChatUUID);
      }
    };
    
    // Comprobar la conexión cada 30 segundos
    const interval = setInterval(checkConnection, 30000);
    
    return () => clearInterval(interval);
  }, [isConnected, isConnecting, selectedChatUUID, lastReconnectAttempt]);

  // Informar al usuario cuando la conexión se pierde
  useEffect(() => {
    if (!isConnected && selectedChatUUID && !isConnecting) {
      antdMessage.warning("La conexión WebSocket se perdió. Intentando reconectar automáticamente...");
    }
  }, [isConnected, selectedChatUUID, isConnecting]);

  /* Función para manejar el cambio de tamaño de la ventana */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /* Al hacer clic en un chat de la lista */
  const handleChatSelect = (chat_uuid: string) => {
    // Si es el mismo chat, no hacemos nada
    if (chat_uuid === selectedChatUUID) return;

    // Establecemos isConnecting antes de cualquier operación
    setIsConnecting(true);
    
    // Desactivamos temporalmente las notificaciones de desconexión
    const currentWs = webSocketService.getWebSocket();
    if (currentWs) {
      currentWs.onclose = null;
      currentWs.onerror = null;
    }
    
    // Actualizamos el estado y cargamos el historial
    setSelectedChatUUID(chat_uuid);
    fetchChatHistory(chat_uuid);
    
    // Desconectamos y conectamos al nuevo chat
    webSocketService.disconnect();
    
    // Pequeño delay antes de reconectar para asegurar una desconexión limpia
    setTimeout(() => {
      connectWebSocket(chat_uuid);
    }, 100);
    
    // En dispositivos móviles, mostrar la vista de chat
    if (windowWidth <= 768) {
      setIsMobileChatView(true);
    }
  };

  /* Manejar el botón de volver a la lista en móvil */
  const handleBackToList = () => {
    setIsMobileChatView(false);
  };

  /* Actualizar todos los chats (botón de refresh global) */
  const handleRefreshAllChats = async () => {
    setIsRefreshing(true);
    try {
      await getChatData();
      antdMessage.success("Historiales de chats actualizados correctamente.");
    } catch (error) {
      // Manejo de error ya implementado en getChatData
    } finally {
      setIsRefreshing(false);
    }
  };

  /* Refrescar historial del chat seleccionado */
  const handleRefreshSelectedChat = () => {
    if (selectedChatUUID) {
      fetchChatHistory(selectedChatUUID);
      antdMessage.success("Historial de chat recargado.");
    } else {
      antdMessage.warning("No hay un chat seleccionado para recargar.");
    }
  };

  /* Liberar el chat (si está controlado por un agente) */
  const handleLiberarChat = async () => {
    if (!selectedChatUUID) {
      antdMessage.warning("No hay un chat seleccionado para liberar.");
      return;
    }

    setIsLiberating(true); // Iniciar el estado de carga

    try {
      const res = await put("/chat-ai", {
        release_chat: true,
        establishment_id: user_data["custom:establishment_id"],
        chat_uuid: selectedChatUUID,
      });

      if (res.answer) {
        antdMessage.success("Chat liberado exitosamente.");
        setChatList((prevChats) =>
          prevChats.map((chat) =>
            chat.chat_uuid === selectedChatUUID
              ? { ...chat, is_controlled: false }
              : chat
          )
        );
      } else {
        antdMessage.error(res.message || "Error al liberar el chat.");
      }
    } catch (error) {
      antdMessage.error("Error al conectar con la API para liberar el chat.");
    } finally {
      setIsLiberating(false); // Finalizar el estado de carga
    }
  };

  /* useEffects para la configuración inicial */
  useEffect(() => {
    if (user_data["custom:wppConfigured"] === "false") {
      antdMessage.warning("Por favor, configura tu WhatsApp para comenzar.");
      window.location.href = "/dashboard/whatsapp-integration";
    } else if (user_data["custom:plan"] !== "0") {
      getChatData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_data]);

  useEffect(() => {
    return () => {
      webSocketService.disconnect();
    };
  }, []);

  /* Scroll automático al final cada vez que cambian los mensajes */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* Recibir mensajes del WebSocket */
  const handleIncomingWebSocketMessage = (data: any) => {
    console.log("Mensaje recibido por WebSocket:", data);
    setIsTyping(false); // Hide typing indicator when message is received
    const validRoles = ["assistant", "agent", "user"];
    const role = validRoles.includes(data.role) ? data.role : "user";

    const newMessage: Message = {
      id: generateUniqueId(),
      text: data.message,
      timestamp: data.timestamp,
      role,
      message_type: data.message_type || "text",
    };

    setMessages((prevMessages) => [...prevMessages, newMessage]);
  };

  useEffect(() => {
    webSocketService.setOnMessageCallback(handleIncomingWebSocketMessage);
  }, []);

  /* Optimizar el renderizado de la lista de chats */
  const filteredChatList = useMemo(() => {
    return chatList.filter(
      (chat) =>
        chat.name_user.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.chat_uuid.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [chatList, searchQuery]);

  /* Chat seleccionado memoizado */
  const selectedChat = useMemo(() => {
    return chatList.find(
      (chat) => chat.chat_uuid === selectedChatUUID
    );
  }, [chatList, selectedChatUUID]);

  /* Manejo de Emojis */
  const addEmoji = (emoji: any) => {
    setNewMessage((prevMessage) => prevMessage + emoji.native);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  /* Alternar entre vista de chat y lista en móvil */
  const toggleMobileView = useCallback(() => {
    setIsMobileChatView(prev => !prev);
  }, []);

  /* Manejar el enfoque del input */
  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  return (
    <div 
      className={`whatsapp-chat-wrapper ${isMobileChatView ? 'whatsapp-mobile-chat-view' : ''} ${isInputFocused ? 'whatsapp-input-focused' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sidebar Izquierdo: Lista de Chats */}
      <div className="whatsapp-chat-sidebar">
        <div className="whatsapp-chat-sidebar-header">
          <h3>Historial de Chats</h3>
          <Tooltip title="Actualizar todos los historiales">
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleRefreshAllChats}
              loading={isRefreshing}
              title="Actualizar todos los historiales"
            />
          </Tooltip>
        </div>

        {/* Buscador de chats */}
        <Input
          placeholder="Buscar chats"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          className="whatsapp-search-input"
          prefix={<SearchOutlined />}
        />

        {loadingChats ? (
          <div className="whatsapp-chat-list-loading">
            {/* Shimmer para lista de chats */}
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="whatsapp-chat-list-loading-item">
                <div className="whatsapp-shimmer" style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '12px' }}></div>
                <div style={{ flex: 1 }}>
                  <div className="whatsapp-shimmer" style={{ width: '70%', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="whatsapp-shimmer" style={{ width: '40%', height: '12px', borderRadius: '4px' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredChatList.length === 0 ? (
          <div className="whatsapp-no-chats">No se encontraron chats.</div>
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filteredChatList}
            renderItem={(chat: ChatItem) => (
              <List.Item
                className={`ant-list-item whatsapp-chat-item ${
                  selectedChatUUID === chat.chat_uuid ? "whatsapp-whatsapp-selected-chat" : ""
                }`}
                onClick={() => handleChatSelect(chat.chat_uuid)}
              >
                <List.Item.Meta
                  avatar={
                    <Badge 
                      dot 
                      color={chat.is_controlled ? "#FF9800" : "#25D366"} 
                      offset={[-2, 36]}
                    >
                      <Avatar icon={<UserOutlined />} />
                    </Badge>
                  }
                  title={
                    <span
                      className="ant-list-item-meta-title"
                      style={{
                        fontWeight: "bold",
                        color:
                          selectedChatUUID === chat.chat_uuid
                            ? "#25D366"
                            : "#333",
                      }}
                    >
                      {chat.name_user}
                      {/* Icono con Tooltip para indicar que está controlado por un agente */}
                      {chat.is_controlled && (
                        <Tooltip title="Este chat está siendo atendido por un agente">
                          <span className="whatsapp-agent-controlled-badge">
                            <CustomerServiceOutlined
                              style={{ marginRight: 4 }}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  }
                  description={
                    <span
                      style={{
                        color:
                          selectedChatUUID === chat.chat_uuid
                            ? "#25D366"
                            : "#666",
                      }}
                    >
                      {`ID: ${chat.chat_uuid.substring(0, 12)}...`}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>

      {/* Área Principal de Chat */}
      <div className="whatsapp-chat-container">
        {/* Header del chat */}
        <div className="whatsapp-chat-header">
          {/* Botón para volver a la lista en móvil */}
          {windowWidth <= 768 && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToList}
              className="whatsapp-chat-back-button"
            />
          )}

          <Avatar size="large" icon={<WhatsAppOutlined />} className="whatsapp-mr-2" />
          <div className="whatsapp-chat-header-info">
            <div className="whatsapp-chat-name">
              {selectedChat ? selectedChat.name_user : "Selecciona un chat"}
            </div>
            {selectedChatUUID && lastActive && (
              <div className="whatsapp-chat-last-active">
                Última actividad: {moment(lastActive).fromNow()}
              </div>
            )}
          </div>

          {user_data["custom:plan"] !== "0" && (
            <div className="whatsapp-connection-status-wrapper">
              <div
                className={`whatsapp-connection-status-circle ${
                  isConnecting
                    ? "whatsapp-connecting-circle"
                    : isConnected
                    ? "whatsapp-connected-circle"
                    : "whatsapp-disconnected-circle"
                }`}
              ></div>
              <div className="whatsapp-connection-status-text">
                {isConnecting
                  ? "Conectando..."
                  : isConnected
                  ? "Conectado"
                  : "Desconectado"}
              </div>
            </div>
          )}

          {user_data["custom:plan"] === "0" ? (
            <Button
              type="primary"
              icon={<CrownOutlined />}
              onClick={() => (window.location.href = "/plans")}
              style={{ marginLeft: "10px" }}
            >
              Actualízate a Premium
            </Button>
          ) : (
            selectedChatUUID &&
            !isConnected &&
            !isConnecting && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => connectWebSocket(selectedChatUUID)}
                style={{ marginLeft: "10px" }}
                disabled={isConnecting}
              >
                Reconectar
              </Button>
            )
          )}

          {/* Botón para recargar solo el historial de este chat */}
          {selectedChatUUID && (
            <Tooltip title="Recargar historial del chat">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={handleRefreshSelectedChat}
                style={{ marginLeft: "10px" }}
                className="whatsapp-header-icon-button"
              />
            </Tooltip>
          )}

          {/* Botón para liberar el chat si está controlado */}
          {selectedChat && selectedChat.is_controlled && (
            <Tooltip title="Liberar chat">
              <Button
                type="text"
                icon={<UnlockOutlined />}
                onClick={handleLiberarChat}
                style={{ marginLeft: "10px" }}
                className="whatsapp-header-icon-button"
                loading={isLiberating}
              />
            </Tooltip>
          )}
        </div>

        {/* Área donde se muestran los mensajes */}
        <div className="whatsapp-chat-messages">
          {loadingHistory ? (
            <div className="whatsapp-messages-loading-overlay">
              <div style={{ width: '90%', maxWidth: '600px' }}>
                {/* Mensaje del usuario (izquierda) */}
                <div className="whatsapp-shimmer-message">
                  <div className="whatsapp-shimmer whatsapp-shimmer-message-avatar"></div>
                  <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '60%' }}>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-bubble" style={{ width: '230px', height: '18px', marginBottom: '8px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-content" style={{ width: '280px', height: '50px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-time" style={{ width: '80px', height: '10px', alignSelf: 'flex-end', marginTop: '5px' }}></div>
                  </div>
                </div>
                
                {/* Mensaje del bot (derecha) */}
                <div className="whatsapp-shimmer-message" style={{ justifyContent: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '70%', alignItems: 'flex-end' }}>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-bubble" style={{ width: '180px', height: '18px', marginBottom: '8px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-content" style={{ width: '320px', height: '70px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-time" style={{ width: '80px', height: '10px', marginTop: '5px' }}></div>
                  </div>
                  <div className="whatsapp-shimmer whatsapp-shimmer-message-avatar"></div>
                </div>
                
                {/* Otro mensaje del usuario */}
                <div className="whatsapp-shimmer-message">
                  <div className="whatsapp-shimmer whatsapp-shimmer-message-avatar"></div>
                  <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '50%' }}>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-bubble" style={{ width: '200px', height: '18px', marginBottom: '8px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-content" style={{ width: '240px', height: '40px' }}></div>
                    <div className="whatsapp-shimmer whatsapp-shimmer-message-time" style={{ width: '80px', height: '10px', alignSelf: 'flex-end', marginTop: '5px' }}></div>
                  </div>
                </div>
              </div>
            </div>
          ) : !selectedChatUUID ? (
            <div className="whatsapp-no-chat-selected">
              <div className="whatsapp-empty-state-icon">💬</div>
              Selecciona un chat para cargar la conversación.
            </div>
          ) : messages.length === 0 ? (
            <div className="whatsapp-no-messages">
              <div className="whatsapp-empty-state-icon">📩</div>
              No hay mensajes en este chat.
            </div>
          ) : (
            <>
              <div className="whatsapp-chat-date-divider">
                <span>{moment(messages[0].timestamp).format("DD MMMM, YYYY")}</span>
              </div>
              {messages.map((msg: Message, index) => {
                // Check if we need to add a date divider
                const showDateDivider = index > 0 && 
                  moment(msg.timestamp).format('YYYY-MM-DD') !== 
                  moment(messages[index-1].timestamp).format('YYYY-MM-DD');
                
                return (
                  <React.Fragment key={msg.id}>
                    {showDateDivider && (
                      <div className="whatsapp-chat-date-divider">
                        <span>{moment(msg.timestamp).format("DD MMMM, YYYY")}</span>
                      </div>
                    )}
                    <ChatMessage message={msg} setPreviewImage={setPreviewImage} />
                  </React.Fragment>
                );
              })}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="whatsapp-chat-message whatsapp-received">
                  <Avatar
                    icon={<RobotOutlined />}
                    className="whatsapp-message-avatar"
                    style={{ backgroundColor: "#273c75" }}
                  />
                  <div className="whatsapp-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Ref para el scroll */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input para escribir mensajes */}
        <ChatInput 
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessage}
          isSending={isSending}
          isConnected={isConnected}
          showEmojiPicker={showEmojiPicker}
          toggleEmojiPicker={toggleEmojiPicker}
          addEmoji={addEmoji}
          data={data}
          windowWidth={windowWidth}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />

        {/* Indicador visual para móvil */}
        {windowWidth <= 768 && (
          <div className="whatsapp-mobile-active-indicator"></div>
        )}
        
        {/* Botón flotante para móvil */}
        {windowWidth <= 768 && (
          <FloatButton
            icon={isMobileChatView ? <BarsOutlined /> : <CommentOutlined />}
            tooltip={isMobileChatView ? "Ver lista de chats" : "Ver chat activo"}
            onClick={toggleMobileView}
            type="primary"
            className="whatsapp-chat-toggle-button"
            style={{ 
              right: 20, 
              bottom: 100
            }}
          />
        )}
      </div>
      
      {/* Contenedor swipeable para gestos móviles */}
      {windowWidth <= 768 && (
        <div 
          className="whatsapp-swipe-overlay"
          onClick={toggleMobileView}
        ></div>
      )}
    </div>
  );
};

export default WhatsAppChat;